import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { OutageNotificationsClient, type BannerSeverity } from '@@/api/api-outage-notifications';
import type { BannerFormValues } from '../types/banner';

export const useBannerManagement = () => {
  const queryClient = useQueryClient();

  // Query to fetch existing banner
  const {
    data: banner,
    isLoading,
    isFetching,
    isError,
    error
  } = useQuery({
    queryKey: ['banner'],
    queryFn: async () => {
      const client = new OutageNotificationsClient();
      const result = await client.getBanner();

      if (!result.ok) {
        // If no banner exists (404), return null
        return null;
      }
      return result.data;
    },
    retry: (failureCount, error) => {
      // Don't retry on 404 (no banner exists)
      if (error instanceof Error && error.message === 'No banner found') {
        return false;
      }
      return failureCount < 2;
    }
  });

  // Mutation to create or update banner
  type SaveFormData = Omit<BannerFormValues, 'locationInput'>;

  const mutation = useMutation({
    mutationFn: async ({ formData, currentBanner }: { formData: SaveFormData; currentBanner: typeof banner }) => {
      const client = new OutageNotificationsClient();

      // Transform UI data to API format
      const apiData = {
        message: formData.bannerText,
        severity: formData.severity as BannerSeverity,
        pageLocations: formData.selectedLocations,
        isActive: formData.bannerVisibility,
        hideLogin: formData.hideLogin
      };

      if (currentBanner?.bannerId) {
        // Update existing banner
        const result = await client.updateBanner(currentBanner.bannerId, apiData);
        if (!result.ok) {
          throw new Error(result.message);
        }
        return result.data;
      } else {
        // Create new banner
        const result = await client.createBanner(apiData);
        if (!result.ok) {
          throw new Error(result.message);
        }
        return result.data;
      }
    },
    onSuccess: () => {
      // Refetch banner data after successful save
      queryClient.invalidateQueries({ queryKey: ['banner'] });
      queryClient.invalidateQueries({ queryKey: ['active-banner'] });
      toast.success('Banner saved successfully');
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : 'Failed to save banner';
      toast.error(message);
    }
  });

  // Derive form data from banner
  const formData: BannerFormValues | null = banner
    ? {
        bannerVisibility: banner.isActive,
        severity: banner.severity,
        bannerText: banner.message,
        selectedLocations: banner.pageLocations,
        hideLogin: banner.hideLogin,
        locationInput: ''
      }
    : null;

  const saveBanner = (
    formData: SaveFormData,
    callbacks?: { onSuccess?: () => void; onError?: (error: Error) => void }
  ) => {
    // basic validation centralized here
    if (!formData.bannerText.trim()) {
      toast.error('Banner text is required');
      return;
    }
    if (!formData.severity) {
      toast.error('Message severity is required');
      return;
    }

    mutation.mutate(
      { formData, currentBanner: banner },
      {
        onSuccess: () => {
          callbacks?.onSuccess?.();
        },
        onError: (error) => {
          callbacks?.onError?.(error as Error);
        }
      }
    );
  };

  return {
    formData,
    isLoading,
    isFetching,
    isError,
    error,
    saveBanner,
    isSaving: mutation.isPending
  };
};
